'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTrackFormAction } from '@nwwp/segment-analytics'
import { useVeritonic } from '@nwwp/veritonic'
import { createLead } from '~/actions/nwwp-api'
import { useBrowserNavigation } from '~/hooks/useBrowserNavigation'
import type { FunnelSchema } from '~/lib/schemas/funnel-schema'
import { useFunnelStore } from './use-funnel-store'
import { type ConfigConditions, type FlattenedStep, getFlattenSteps, type Steps } from '../utils'

export const useFunnel = <T extends ConfigConditions>(steps: Steps<T>, conditions: T = {} as T) => {
  const { _hasHydrated, setData, data, isQualifiedForNWWP, tracking } = useFunnelStore()
  const { trackFlowStart, isReady: veritonicReady } = useVeritonic()
  const trackFormAction = useTrackFormAction()
  // eslint-disable-next-line react-hooks/use-memo -- Conditions are plain booleans; step definitions are static.
  const flattenSteps = useMemo(() => getFlattenSteps(steps, conditions), [JSON.stringify(conditions)])
  const [currentStep, setCurrentStep] = useState<FlattenedStep | null>(null)
  const shouldGoToNextStep = useRef(false)
  const onCompleteFunnelRan = useRef(false)
  const funnelStartedEmitted = useRef(false)
  const maxSteps = useRef(0)

  const handleSubmit = async (stepData: Partial<FunnelSchema>) => {
    if (currentStep?.type === 'step') {
      currentStep.render.formActionFields
        ?.filter((field) => field in stepData)
        .forEach((field) =>
          trackFormAction({
            questionName: field,
            questionValue: stepData[field],
            formName: 'find-your-advisor',
            stepName: currentStep.path,
          })
        )
    }

    setData(stepData)
    shouldGoToNextStep.current = true
  }

  const handlePrevious = () => {
    if (!currentStep) return
    const previousStep = flattenSteps[flattenSteps.findIndex((step) => step.path === currentStep.path) - 1]
    if (previousStep) setCurrentStep(previousStep)
  }

  const { pushBrowserState } = useBrowserNavigation({
    onBackClick: handlePrevious,
    onForwardClick: () => handleSubmit({}),
  })

  const isFirstStep = !currentStep || currentStep.path === flattenSteps[0]?.path
  const isTerminalStep = currentStep?.type === 'terminal'
  const isInBetweenStep = !isFirstStep && !isTerminalStep
  const progressPercentage = currentStep
    ? // eslint-disable-next-line react-hooks/refs -- maxSteps is set once from the static step list; it never changes across renders
      ((flattenSteps.findIndex((step) => step.path === currentStep.path) + 1) / maxSteps.current) * 100
    : 0

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- seeds the first step once after zustand hydration
    if (_hasHydrated && !currentStep) setCurrentStep(flattenSteps[0] ?? null)
  }, [_hasHydrated, flattenSteps, currentStep])

  useEffect(() => {
    if (!currentStep || !shouldGoToNextStep.current) return

    const nextStep = flattenSteps[flattenSteps.findIndex((step) => step.path === currentStep.path) + 1]
    if (nextStep) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- funnel advance is driven by the submitted-data effect, not by render
      setCurrentStep(nextStep)
      pushBrowserState()
    }

    shouldGoToNextStep.current = false
  }, [data, flattenSteps, currentStep, pushBrowserState])

  useEffect(() => {
    if (!isTerminalStep || onCompleteFunnelRan.current) return
    if (isQualifiedForNWWP()) void createLead(data as FunnelSchema, tracking)
    onCompleteFunnelRan.current = true
  }, [isTerminalStep, isQualifiedForNWWP, data, tracking])

  useEffect(() => {
    if (!maxSteps.current) maxSteps.current = flattenSteps.length
  }, [flattenSteps])

  useEffect(() => {
    if (currentStep && currentStep.path === flattenSteps[0]?.path && !funnelStartedEmitted.current && veritonicReady) {
      funnelStartedEmitted.current = true
      trackFlowStart()
    }
  }, [currentStep, flattenSteps, veritonicReady, trackFlowStart])

  return { currentStep, progressPercentage, isFirstStep, isTerminalStep, isInBetweenStep, handleSubmit, handlePrevious }
}
